import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { Card } from '../../components/ui/card';
import { employeeAPI } from '../../services/api';
import { useMessages } from '../../contexts/MessageContext';
import { Building, ChevronRight, Loader2, Mail } from 'lucide-react';

const Messages = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const { refreshUnreadCount } = useMessages();

  useEffect(() => {
    fetchMessages();
  }, []);

const fetchMessages = async () => {
  try {
    const res = await employeeAPI.getMessages();
    setMessages(res.data);
    
    try {
      await employeeAPI.markAllMessagesRead();
      // Tell the local state everything is now read
      setMessages(res.data.map(m => ({ ...m, read: true })));
      await refreshUnreadCount();
    } catch (markError) {
      console.error('Error marking messages as read:', markError);
    }
  } catch (error) {
    console.error('Error fetching messages:', error);
  } finally {
    setLoading(false);
  }
};

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-[#1B5AFF]" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-8" data-testid="employee-messages">
        {/* Header */}
        <div className="animate-fade-in">
          <h1 className="text-3xl font-bold text-[#0A0F1E]">Messages</h1>
          <p className="mt-2 text-[#5B6472]">Employer reach-outs and conversations.</p>
        </div>

        {/* Messages List */}
        <div className="space-y-3">
          {messages.length === 0 ? (
            <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-8 text-center">
              <Mail className="w-12 h-12 text-[#5B6472] mx-auto mb-4" />
              <h3 className="text-lg font-medium text-[#0A0F1E] mb-2">No messages yet</h3>
              <p className="text-[#5B6472]">
                When employers reach out to you, their messages will appear here.
              </p>
            </Card>
          ) : (
            messages.map((msg, i) => (
              <Link
                key={msg.id}
                to={`/employee/messages/${msg.id}`}
                className="block animate-fade-in"
                style={{ animationDelay: `${i * 0.05}s` }}
                data-testid={`message-${msg.id}`}
              >
                <Card className={`bg-[#FFFFFF] border rounded-xl p-4 hover:border-[#1B5AFF]/50 transition-colors group ${
                  msg.read ? 'border-[#E8EDF5]' : 'border-[#1B5AFF]/30 bg-[#1B5AFF]/5'
                }`}>
                  <div className="flex items-center gap-4">
                    {msg.sender_logo ? (
                      <img src={msg.sender_logo} alt="" className="w-12 h-12 rounded-lg object-cover border border-[#E8EDF5]" />
                    ) : (
                      <div className="w-12 h-12 rounded-lg bg-[#F5F8FF] border border-[#E8EDF5] flex items-center justify-center">
                        <Building className="w-5 h-5 text-[#5B6472]" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className={`font-semibold group-hover:text-[#1B5AFF] transition-colors ${msg.read ? 'text-[#0A0F1E]' : 'text-[#1B5AFF]'}`}>
                          {msg.sender_name || 'Unknown Company'}
                        </h3>
                        {!msg.read && (
                          <span className="w-2 h-2 rounded-full bg-[#1B5AFF]" />
                        )}
                      </div>
                      <p className="text-[#5B6472] text-sm truncate mt-1">
                        {msg.content}
                      </p>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-[#5B6472] text-xs">
                        {new Date(msg.created_at).toLocaleDateString()}
                      </span>
                      <ChevronRight className="w-5 h-5 text-[#5B6472] group-hover:text-[#1B5AFF] transition-colors" />
                    </div>
                  </div>
                </Card>
              </Link>
            ))
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default Messages;
